import { sound as pixiSound } from '@pixi/sound';

const DB_NAME = 'autograph-timeline-sprite-cache';
const DB_VERSION = 1;
const STORE_NAME = 'trackSprites';
const CACHE_VERSION = 11;
const MAX_CACHE_ENTRIES = 250;

const DEFAULT_FRAME_WIDTH = 96;
const DEFAULT_FRAME_HEIGHT = 54;
const DEFAULT_JPEG_QUALITY = 0.74;
const DEFAULT_EVENT_TIMEOUT_MS = 15000;


const inMemoryCache = new Map();
const inflightRequests = new Map();
let dbPromise = null;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hashString = (value) => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(36);
};

const buildCacheKey = (descriptor) => {
  return `v${CACHE_VERSION}:${hashString(JSON.stringify(descriptor))}`;
};

const isImageLikeSource = (url = '') => {
  if (!url || typeof url !== 'string') return false;
  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.startsWith('data:image') ||
    /\.(png|jpe?g|webp|gif|bmp|svg)(\?|#|$)/i.test(lowerUrl)
  );
};

const openTimelineSpriteDb = async () => {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return null;
  }

  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('lastAccessedAt', 'lastAccessedAt', { unique: false });
        } else {
          const tx = request.transaction;
          const store = tx.objectStore(STORE_NAME);
          if (!store.indexNames.contains('lastAccessedAt')) {
            store.createIndex('lastAccessedAt', 'lastAccessedAt', { unique: false });
          }
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch (error) {
      console.warn('Timeline sprite DB unavailable:', error);
      resolve(null);
    }
  });

  return dbPromise;
};

const getCachedFrames = async (key) => {
  const db = await openTimelineSpriteDb();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const record = request.result;
        if (!record || !Array.isArray(record.frames) || record.frames.length === 0) {
          resolve(null);
          return;
        }

        record.lastAccessedAt = Date.now();
        store.put(record);
        resolve(record.frames);
      };

      request.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch (error) {
      resolve(null);
    }
  });
};

const pruneCacheIfNeeded = async () => {
  const db = await openTimelineSpriteDb();
  if (!db) return;

  await new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const countRequest = store.count();

      countRequest.onsuccess = () => {
        const total = countRequest.result || 0;
        const excess = total - MAX_CACHE_ENTRIES;

        if (excess <= 0) {
          resolve();
          return;
        }

        const index = store.index('lastAccessedAt');
        let removed = 0;
        const cursorRequest = index.openCursor();

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor || removed >= excess) {
            resolve();
            return;
          }
          store.delete(cursor.primaryKey);
          removed += 1;
          cursor.continue();
        };

        cursorRequest.onerror = () => resolve();
      };

      countRequest.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch (error) {
      resolve();
    }
  });
};

const saveCachedFrames = async (key, frames, descriptor) => {
  const db = await openTimelineSpriteDb();
  if (!db) return;

  await new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const now = Date.now();
      store.put({
        key,
        frames,
        descriptor,
        createdAt: now,
        lastAccessedAt: now,
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch (error) {
      resolve();
    }
  });

  pruneCacheIfNeeded().catch(() => { });
};

const waitForMediaEvent = (element, eventName, timeoutMs = DEFAULT_EVENT_TIMEOUT_MS) => {
  return new Promise((resolve, reject) => {
    let timeoutId = null;

    const cleanup = () => {
      element.removeEventListener(eventName, onSuccess);
      element.removeEventListener('error', onError);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const onSuccess = () => {
      cleanup();
      resolve();
    };

    const onError = () => {
      cleanup();
      reject(new Error(`Failed to load media (${eventName})`));
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for ${eventName}`));
    }, timeoutMs);

    element.addEventListener(eventName, onSuccess, { once: true });
    element.addEventListener('error', onError, { once: true });
  });
};

const drawMediaCover = (ctx, media, targetWidth, targetHeight) => {
  const mediaWidth = media.videoWidth || media.naturalWidth || media.width || 0;
  const mediaHeight = media.videoHeight || media.naturalHeight || media.height || 0;

  ctx.clearRect(0, 0, targetWidth, targetHeight);

  if (!mediaWidth || !mediaHeight) {
    ctx.fillStyle = '#202020';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    return;
  }

  const mediaAspect = mediaWidth / mediaHeight;
  const targetAspect = targetWidth / targetHeight;

  let drawWidth = targetWidth;
  let drawHeight = targetHeight;
  let offsetX = 0;
  let offsetY = 0;

  if (mediaAspect > targetAspect) {
    drawHeight = targetHeight;
    drawWidth = drawHeight * mediaAspect;
    offsetX = (targetWidth - drawWidth) / 2;
  } else {
    drawWidth = targetWidth;
    drawHeight = drawWidth / mediaAspect;
    offsetY = (targetHeight - drawHeight) / 2;
  }

  ctx.drawImage(media, offsetX, offsetY, drawWidth, drawHeight);
};

const loadImage = (sourceUrl) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load image from ${sourceUrl}`));
    image.src = sourceUrl;
  });
};

const createImageFrames = async ({
  sourceUrl,
  frameCount,
  frameWidth,
  frameHeight,
}) => {
  if (!sourceUrl) return [];

  const image = await loadImage(sourceUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];

  canvas.width = frameWidth;
  canvas.height = frameHeight;

  drawMediaCover(ctx, image, frameWidth, frameHeight);

  let dataUrl = '';
  try {
    dataUrl = canvas.toDataURL('image/jpeg', DEFAULT_JPEG_QUALITY);
  } catch (error) {
    console.warn('Failed to serialize image timeline sprite:', error);
    return [];
  }

  return Array.from({ length: frameCount }, () => dataUrl);
};

const getStandaloneAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  try {
    return new AudioContextClass();
  } catch (error) {
    return null;
  }
};

const closeAudioContext = async (audioContext) => {
  if (!audioContext || typeof audioContext.close !== 'function') return;
  try {
    await audioContext.close();
  } catch (error) {
    // Ignore close failures from already-closed contexts.
  }
};

const decodeAudioBufferWithPixiSound = async (arrayBuffer) => {
  if (!arrayBuffer || arrayBuffer.byteLength === 0) return null;
  if (!pixiSound || pixiSound.useLegacy) return null;

  const context = pixiSound.context;
  if (!context || typeof context.decode !== 'function') return null;

  return new Promise((resolve, reject) => {
    try {
      // Use a copy because some browsers detach the source buffer after decode.
      context.decode(arrayBuffer.slice(0), (error, decodedBuffer) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(decodedBuffer || null);
      });
    } catch (error) {
      reject(error);
    }
  });
};

const decodeAudioBufferWithStandaloneContext = async (arrayBuffer) => {
  const audioContext = getStandaloneAudioContext();
  if (!audioContext) return null;

  try {
    return await audioContext.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    await closeAudioContext(audioContext);
  }
};


const createAudioWaveformFrames = async ({
  sourceUrl,
  startTimeSec,
  durationSec,
  frameCount,
  frameWidth,
  frameHeight,
}) => {
  if (!sourceUrl) return [];

  const response = await fetch(sourceUrl);
  if (!response.ok) {
    throw new Error(`Audio fetch failed (${response.status})`);
  }

  const arrayBuffer = await response.arrayBuffer();
  if (!arrayBuffer || arrayBuffer.byteLength === 0) {
    return [];
  }

  let audioBuffer = null;
  try {
    audioBuffer = await decodeAudioBufferWithPixiSound(arrayBuffer);
  } catch (error) {
    console.warn('Pixi Sound decode failed for timeline waveform, falling back:', error);
  }

  if (!audioBuffer) {
    audioBuffer = await decodeAudioBufferWithStandaloneContext(arrayBuffer);
  }

  if (!audioBuffer) {
    throw new Error('Unable to decode audio for waveform preview');
  }

  const channels = [];
  for (let i = 0; i < (audioBuffer.numberOfChannels || 0); i += 1) {
    const data = audioBuffer.getChannelData(i);
    if (data && data.length > 0) {
      channels.push(data);
    }
  }

  const primaryChannel = channels[0] || null;
  if (!primaryChannel || primaryChannel.length === 0) {
    return [];
  }

  const sampleRate = audioBuffer.sampleRate || 44100;
  const totalDuration = Number.isFinite(audioBuffer.duration) && audioBuffer.duration > 0
    ? audioBuffer.duration
    : primaryChannel.length / sampleRate;

  const clipStart = clamp(startTimeSec || 0, 0, Math.max(0, totalDuration - 0.001));
  const clipDuration = Math.max(0.1, durationSec || totalDuration);
  const sourceSampleCount = Math.max(1, primaryChannel.length);
  const startSample = clamp(Math.floor(clipStart * sampleRate), 0, sourceSampleCount - 1);
  // Number of unique samples in the audio (from startSample to end of buffer).
  // When the track is longer than the remaining audio, the waveform loops.
  const uniqueSampleSpan = sourceSampleCount - startSample;
  // Total samples the timeline track spans (may exceed the audio buffer — loops)
  const trackSampleCount = Math.max(1, Math.ceil(clipDuration * sampleRate));

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];

  canvas.width = frameWidth;
  canvas.height = frameHeight;

  // Build a master canvas with bar-chart style waveform, then slice into frames.
  // This gives each frame a unique section of audio (no repeated bars across tiles).
  // When the track extends beyond the audio duration, the waveform repeats (loops).
  const barWidth = 5;
  const barGap = 2;
  const barStep = barWidth + barGap;
  const masterWidth = clamp(Math.round(frameCount * frameWidth * 2), 1024, 4096);
  const masterCanvas = document.createElement('canvas');
  const masterCtx = masterCanvas.getContext('2d');
  if (!masterCtx) return [];
  masterCanvas.width = masterWidth;
  masterCanvas.height = frameHeight;

  const totalBars = Math.floor(masterWidth / barStep);
  const samplesPerBar = Math.max(1, trackSampleCount / totalBars);

  // Compute peak amplitude per bar (using absolute peak, not RMS, for a dynamic look).
  // Samples wrap around the audio buffer from the crop start point for looping.
  const peakByBar = new Float32Array(totalBars);
  let absolutePeak = 0;

  for (let bar = 0; bar < totalBars; bar += 1) {
    const barStartSample = Math.floor(bar * samplesPerBar);
    const barEndSample = Math.min(trackSampleCount, Math.ceil((bar + 1) * samplesPerBar));
    let peak = 0;
    for (let s = barStartSample; s < barEndSample; s += 1) {
      // Wrap within the audio range starting from startSample.
      // This loops the audio from the crop point, not from sample 0.
      const si = startSample + (s % uniqueSampleSpan);
      let mixed = 0;
      for (let ch = 0; ch < channels.length; ch += 1) {
        mixed += channels[ch][si] || 0;
      }
      mixed /= Math.max(1, channels.length);
      const abs = Math.abs(mixed);
      if (abs > peak) peak = abs;
    }
    peakByBar[bar] = peak;
    if (peak > absolutePeak) absolutePeak = peak;
  }

  // Normalize using 98th percentile so one loud spike doesn't flatten everything else
  const sortedPeaks = Array.from(peakByBar).sort((a, b) => a - b);
  const p98idx = clamp(Math.floor(sortedPeaks.length * 0.98), 0, sortedPeaks.length - 1);
  const p98peak = sortedPeaks[p98idx] || absolutePeak;
  const normalizer = Math.max(0.0001, Math.max(p98peak, absolutePeak * 0.25));

  // Draw bars on master canvas — symmetric from center, mint green background
  masterCtx.fillStyle = '#9fd6be';
  masterCtx.fillRect(0, 0, masterWidth, frameHeight);

  const centerY = frameHeight / 2;
  const maxHalfH = (frameHeight / 2) * 0.9;

  for (let bar = 0; bar < totalBars; bar += 1) {
    const normalized = clamp(peakByBar[bar] / normalizer, 0, 1);
    // Gamma curve: boosts quieter sounds so they're visible, compresses peaks
    const shaped = Math.pow(normalized, 0.6);
    const halfH = Math.max(1.5, shaped * maxHalfH);
    const x = bar * barStep;
    const alpha = 0.55 + shaped * 0.4;
    masterCtx.fillStyle = `rgba(20, 120, 60, ${alpha.toFixed(2)})`;
    masterCtx.fillRect(x, centerY - halfH, barWidth, halfH * 2);
  }

  // Slice master canvas into frames. The master canvas represents the full track
  // duration (with looping waveform beyond the audio end), so each frame is a
  // direct linear slice.
  const frames = [];
  const sliceWidth = masterWidth / frameCount;

  for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
    const srcX = frameIndex * sliceWidth;
    ctx.clearRect(0, 0, frameWidth, frameHeight);
    ctx.drawImage(masterCanvas, srcX, 0, sliceWidth, frameHeight, 0, 0, frameWidth, frameHeight);
    frames.push(canvas.toDataURL('image/png'));
  }

  return frames;
};

const seekVideo = async (video, targetTime) => {
  const maxTime = Number.isFinite(video.duration) && video.duration > 0
    ? Math.max(0, video.duration - 0.001)
    : Number.POSITIVE_INFINITY;
  const safeTime = clamp(targetTime, 0, maxTime);

  if (Math.abs(video.currentTime - safeTime) < 0.015) {
    return;
  }

  const seekPromise = waitForMediaEvent(video, 'seeked', DEFAULT_EVENT_TIMEOUT_MS);
  video.currentTime = safeTime;
  await seekPromise;
};

const createVideoFrames = async ({
  sourceUrl,
  posterUrl,
  startTimeSec,
  durationSec,
  frameCount,
  frameWidth,
  frameHeight,
}) => {
  if (!sourceUrl) return [];

  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.preload = 'auto';
  video.playsInline = true;
  video.src = sourceUrl;

  try {
    await waitForMediaEvent(video, 'loadedmetadata', DEFAULT_EVENT_TIMEOUT_MS);
    if (video.readyState < 2) {
      await waitForMediaEvent(video, 'loadeddata', DEFAULT_EVENT_TIMEOUT_MS);
    }

    const mediaDuration = Number.isFinite(video.duration) && video.duration > 0
      ? video.duration
      : Math.max(startTimeSec + durationSec, 0.1);

    const clipStart = clamp(startTimeSec || 0, 0, Math.max(0, mediaDuration - 0.001));
    const clipDuration = Math.max(0.1, durationSec || mediaDuration);
    const clipEnd = clamp(clipStart + clipDuration, clipStart, mediaDuration);
    const span = Math.max(0, clipEnd - clipStart);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return [];

    canvas.width = frameWidth;
    canvas.height = frameHeight;

    const frames = [];

    for (let i = 0; i < frameCount; i += 1) {
      const sampleTime = frameCount <= 1
        ? clipStart
        : clipStart + ((span * i) / (frameCount - 1));

      await seekVideo(video, sampleTime);
      drawMediaCover(ctx, video, frameWidth, frameHeight);

      try {
        frames.push(canvas.toDataURL('image/jpeg', DEFAULT_JPEG_QUALITY));
      } catch (error) {
        console.warn('Video frame serialization failed for timeline sprites:', error);
        throw error;
      }
    }

    if (frames.length > 0) {
      return frames;
    }
  } catch (error) {
    console.warn('Video timeline sprite extraction failed, falling back to poster:', error);
  } finally {
    video.pause();
    video.removeAttribute('src');
    video.load();
  }

  if (posterUrl) {
    return createImageFrames({
      sourceUrl: posterUrl,
      frameCount,
      frameWidth,
      frameHeight,
    });
  }

  return [];
};

const generateFrames = async (descriptor) => {
  const {
    mediaType,
    sourceUrl,
    posterUrl,
    startTimeSec,
    durationSec,
    frameCount,
    frameWidth,
    frameHeight,
  } = descriptor;

  const safeFrameCount = clamp(frameCount, 1, 18);

  if (mediaType === 'video' && sourceUrl && !isImageLikeSource(sourceUrl)) {
    return createVideoFrames({
      sourceUrl,
      posterUrl,
      startTimeSec,
      durationSec,
      frameCount: safeFrameCount,
      frameWidth,
      frameHeight,
    });
  }

  if (mediaType === 'audio') {
    if (sourceUrl && !isImageLikeSource(sourceUrl)) {
      try {
        const waveformFrames = await createAudioWaveformFrames({
          sourceUrl,
          startTimeSec,
          durationSec,
          frameCount: safeFrameCount,
          frameWidth,
          frameHeight,
        });

        if (waveformFrames.length > 0) {
          return waveformFrames;
        }
      } catch (error) {
        console.warn('Audio waveform timeline extraction failed, falling back to poster:', error);
      }
    }

    const audioPoster = posterUrl || (isImageLikeSource(sourceUrl) ? sourceUrl : null);
    if (!audioPoster) return [];
    return createImageFrames({
      sourceUrl: audioPoster,
      frameCount: safeFrameCount,
      frameWidth,
      frameHeight,
    });
  }

  const imageSource = sourceUrl || posterUrl;
  if (!imageSource) return [];

  return createImageFrames({
    sourceUrl: imageSource,
    frameCount: safeFrameCount,
    frameWidth,
    frameHeight,
  });
};

export const getTimelineTrackSpriteFrames = async ({
  mediaType,
  sourceUrl,
  posterUrl = null,
  startTimeSec = 0,
  durationSec = 1,
  frameCount = 6,
  frameWidth = DEFAULT_FRAME_WIDTH,
  frameHeight = DEFAULT_FRAME_HEIGHT,
}) => {
  if (!sourceUrl && !posterUrl) return [];

  const descriptor = {
    mediaType: mediaType || 'image',
    sourceUrl: sourceUrl || null,
    posterUrl,
    startTimeSec: Number.isFinite(startTimeSec) ? Number(startTimeSec.toFixed(3)) : 0,
    durationSec: Number.isFinite(durationSec) ? Number(durationSec.toFixed(3)) : 1,
    frameCount: clamp(Math.round(frameCount || 1), 1, 18),
    frameWidth: clamp(Math.round(frameWidth || DEFAULT_FRAME_WIDTH), 32, 256),
    frameHeight: clamp(Math.round(frameHeight || DEFAULT_FRAME_HEIGHT), 18, 256),
  };

  const key = buildCacheKey(descriptor);

  if (inMemoryCache.has(key)) {
    return inMemoryCache.get(key);
  }

  if (inflightRequests.has(key)) {
    return inflightRequests.get(key);
  }

  const requestPromise = (async () => {
    const indexedDbFrames = await getCachedFrames(key);
    if (indexedDbFrames && indexedDbFrames.length > 0) {
      inMemoryCache.set(key, indexedDbFrames);
      return indexedDbFrames;
    }

    const frames = await generateFrames(descriptor);
    if (frames.length > 0) {
      inMemoryCache.set(key, frames);
      await saveCachedFrames(key, frames, descriptor);
    }

    return frames;
  })().finally(() => {
    inflightRequests.delete(key);
  });

  inflightRequests.set(key, requestPromise);
  return requestPromise;
};

export const clearTimelineTrackSpriteMemoryCache = () => {
  inMemoryCache.clear();
};
